import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import "./LogActionModal.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function LogContactModal({ isOpen, onClose, recruit, onContactLogged }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [contactDate, setContactDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [contactTime, setContactTime] = useState(
    new Date().toTimeString().slice(0, 5)
  );
  const [wasSuccessful, setWasSuccessful] = useState(false);
  const [contactMethod, setContactMethod] = useState("Phone");

  const handleLogContact = async () => {
    if (!recruit) return;

    setLoading(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      // Combine date and time into ISO string
      const contactDateTime = new Date(`${contactDate}T${contactTime}`);

      const response = await fetch(
        getApiEndpoint(`/v2/recruiting/leads/${recruit.id}/log-contact`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            notes: notes.trim(),
            contact_date: contactDateTime.toISOString(),
            was_successful: wasSuccessful,
            contact_method: contactMethod,
          }),
        }
      );

      if (response.ok) {
        showSuccess("Contact logged successfully");
        handleClose();
        if (onContactLogged) onContactLogged(recruit);
      } else {
        showError("Failed to log contact");
      }
    } catch (error) {
      handleGenericError(error, "Failed to log contact");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNotes("");
    setContactDate(new Date().toISOString().split("T")[0]);
    setContactTime(new Date().toTimeString().slice(0, 5));
    setWasSuccessful(true);
    setContactMethod("Phone");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="log-action-modal-overlay" onClick={handleClose}>
      <div
        className="log-action-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="log-action-modal-header">
          <h2>Log Contact</h2>
          <button className="log-action-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="log-action-modal-body">
          {recruit && (
            <div className="log-action-info">
              <p className="recruit-name">{recruit.name}</p>
            </div>
          )}

          <div className="action-form">
            <div
              className="contact-form-grid"
              style={{ marginBottom: "0.75rem" }}
            >
              <div className="form-field">
                <label htmlFor="contact-method">Contact Method</label>
                <select
                  id="contact-method"
                  value={contactMethod}
                  onChange={e => setContactMethod(e.target.value)}
                >
                  <option value="Phone">Phone</option>
                  <option value="Text/SMS">Text/SMS</option>
                  <option value="Email">Email</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Instagram">Instagram</option>
                  <option value="In Person">In Person</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="was-successful">Outcome</label>
                <select
                  id="was-successful"
                  value={wasSuccessful ? "true" : "false"}
                  onChange={e => setWasSuccessful(e.target.value === "true")}
                >
                  <option value="false">Attempted</option>
                  <option value="true">Successful</option>
                </select>
              </div>
            </div>

            <details style={{ marginBottom: "0.75rem" }}>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Additional Details
              </summary>

              <div
                className="contact-form-grid"
                style={{ marginBottom: "0.75rem", marginTop: "0.5rem" }}
              >
                <div className="form-field">
                  <label htmlFor="contact-date">Date</label>
                  <input
                    type="date"
                    id="contact-date"
                    value={contactDate}
                    onChange={e => setContactDate(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="contact-time">Time</label>
                  <input
                    type="time"
                    id="contact-time"
                    value={contactTime}
                    onChange={e => setContactTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="notes-section">
                <label htmlFor="contact-notes">Contact Notes</label>
                <textarea
                  id="contact-notes"
                  className="action-notes-textarea"
                  placeholder="Add notes about the contact..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </details>

            <div className="action-form-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleLogContact}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Contact"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogContactModal;
